import * as React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  humanReadableShortPricingUnit,
  Menu,
} from '@rematter/ui-web';
import startCase from 'lodash/startCase';
import {
  LucideCopy,
  LucideGripVertical,
  LucideMoreVertical,
  LucidePlus,
  LucideTrash2,
  LucideX,
} from 'lucide-react';

import type { EvaluatedTierData } from '../../_graphql/queryEvaluateFormulas';
import { useTranslations } from '../../i18n/useTranslations';
import type { VariableRecord } from '../../types/pricing';

import type {
  FlatTierRow,
  NewInlineRowData,
} from './hooks/useEditablePricingTable';
import type { TierEditValues } from './hooks/useProductPricingColumns';
import { PriceField } from './PriceField';
import { SpreadsheetCell } from './SpreadsheetCell';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import {
  SelectMaterial,
  type SelectableMaterial,
} from '~/shared/components/select-fields/SelectMaterial';

const priceFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
  useGrouping: true,
});

const formatEvaluatedPrice = (val: number | null | undefined) => {
  if (val == null) return null;
  return priceFormatter.format(val);
};

type AdHocEntry = { formula: string; value: number };

type SpreadsheetRowProps = {
  row: FlatTierRow;
  rowIndex: number;
  focusState: { row: number; col: number };
  editingState: { row: number; col: number } | null;
  pendingChanges: Map<string, TierEditValues>;
  evaluatedPrices: Record<string, EvaluatedTierData>;
  adHocEvaluated: Map<string, Record<string, AdHocEntry>>;
  variables: VariableRecord[];
  isReadOnly: boolean;
  isCRV: boolean;
  inlineRowData: NewInlineRowData | null;
  onCellChange: (
    tierId: string,
    itemPricingId: string,
    field: string,
    value: string,
  ) => void;
  onInlineRowChange: (
    rowId: string,
    field: string,
    value:
      | string
      | {
          id: string;
          name: string;
          pricingUnit?: string;
          itemGroupName?: string;
        },
  ) => void;
  onAddTier?: (itemPricingId: string) => void;
  onDeleteTier?: (tierId: string) => void;
  onCopy?: (itemPricingId: string) => void;
  onDelete?: (itemPricingId: string) => void;
  getCommonProps: (
    rowIndex: number,
    colIndex: number,
  ) => {
    className: string;
    ref: (el: HTMLDivElement | null) => void;
    onFocus: () => void;
    onBlur: (e: React.FocusEvent) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
  onExitEditing?: () => void;
  onTabNavigate?: (direction?: 'left' | 'right') => void;
  dragHandleProps?: Record<string, unknown>;
  isDragDisabled?: boolean;
  isSelected?: boolean;
  onSelect?: (rowIndex: number) => void;
  hasValidationError?: boolean;
  existingItemIds?: Set<string>;
};

type RowActionMenuProps = {
  isCRV: boolean;
  onAddTier?: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
  t: ReturnType<typeof useTranslations>;
};

//
// ⚛️ ⚛️ ⚛️ ROW ACTION MENU ⚛️ ⚛️ ⚛️
//
const RowActionMenu = ({
  isCRV,
  onAddTier,
  onCopy,
  onDelete,
  t,
}: RowActionMenuProps) => {
  // Memoized values
  const items = React.useMemo(() => {
    const result: {
      label: React.ReactNode;
      onAction: () => void;
      icon?: React.ReactElement<import('lucide-react').LucideProps>;
      className?: string;
    }[] = [];
    if (!isCRV && onAddTier) {
      result.push({
        label: t.pricing.itemPricings.table.addTier,
        onAction: onAddTier,
        icon: <LucidePlus className="size-4" />,
      });
    }
    if (onCopy) {
      result.push({
        label: t.pricing.itemPricings.table.copyItem,
        onAction: onCopy,
        icon: <LucideCopy className="size-4" />,
      });
    }
    if (onDelete) {
      result.push({
        label: t.pricing.itemPricings.table.deleteItem,
        onAction: onDelete,
        icon: <LucideTrash2 className="size-4" />,
        className: 'text-failure',
      });
    }
    return result;
  }, [isCRV, onAddTier, onCopy, onDelete, t]);

  if (items.length === 0) return null;

  return (
    <div
      className="@RowActionMenu flex justify-center"
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Menu
        trigger={
          <Button size="icon" icon={<LucideMoreVertical />} variant="outline" />
        }
        items={items}
      />
    </div>
  );
};

type PriceCellContentProps = {
  rawValue: string;
  evalValue: number | null | undefined;
  originalFormula: string;
  adHocEvaluated: Map<string, Record<string, AdHocEntry>>;
  rowId: string;
  field: 'targetPrice' | 'minPrice' | 'maxPrice';
  isReadOnly: boolean;
  isEditing: boolean;
  variables: VariableRecord[];
  onChange: (value: string) => void;
};

//
// ⚛️ ⚛️ ⚛️ PRICE CELL CONTENT ⚛️ ⚛️ ⚛️
//
const PriceCellContent = ({
  rawValue,
  evalValue,
  originalFormula,
  adHocEvaluated,
  rowId,
  field,
  isReadOnly,
  isEditing,
  variables,
  onChange,
}: PriceCellContentProps) => {
  const isFormulaModified = rawValue !== originalFormula;

  const adHocEntry = adHocEvaluated.get(rowId)?.[field];
  const adHocValue =
    isFormulaModified && adHocEntry?.formula === rawValue
      ? adHocEntry.value
      : null;

  const numericRaw =
    rawValue && /^\d+\.?\d*$/.test(rawValue) ? Number(rawValue) : null;
  const resolveDisplayValue = () => {
    if (adHocValue != null) return formatEvaluatedPrice(adHocValue);
    if (!isFormulaModified && evalValue != null)
      return formatEvaluatedPrice(evalValue);
    if (numericRaw != null) return formatEvaluatedPrice(numericRaw);
    return rawValue || '—';
  };
  const displayValue = resolveDisplayValue();

  if (isReadOnly || !isEditing) {
    return (
      <div className="flex w-full px-2 py-1 text-sm">
        <span className="text-neutral/40">
          {displayValue !== '—' ? '$' : ''}
        </span>
        <span className="flex-1 text-right">{displayValue}</span>
      </div>
    );
  }

  return (
    <PriceField
      value={rawValue}
      onChange={onChange}
      variables={variables}
      isDisabled={isReadOnly}
      className="@PriceCellContent border-none bg-transparent text-right"
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ SPREADSHEET ROW INNER ⚛️ ⚛️ ⚛️
//
const SpreadsheetRowInner = ({
  row,
  rowIndex,
  focusState,
  editingState,
  pendingChanges,
  evaluatedPrices,
  adHocEvaluated,
  variables,
  isReadOnly,
  isCRV,
  inlineRowData,
  onCellChange,
  onInlineRowChange,
  onAddTier,
  onDeleteTier,
  onCopy,
  onDelete,
  getCommonProps,
  onExitEditing,
  onTabNavigate,
  dragHandleProps,
  isDragDisabled,
  isSelected,
  onSelect,
  hasValidationError,
  existingItemIds,
}: SpreadsheetRowProps) => {
  // Translations
  const t = useTranslations();

  const isNewRow = row.isNewRow;
  const isFirstTier = row.isFirstTierOfProduct;
  const isSubsequentTier = !isFirstTier;
  const isEmptyBottom = row.id === 'empty-bottom';

  // Data hooks
  const {
    attributes: sortableAttributes,
    listeners: sortableListeners,
    setNodeRef: setSortableRef,
    transform: sortableTransform,
    transition: sortableTransition,
  } = useSortable({
    id: row.itemPricingId,
    disabled: isDragDisabled || !isFirstTier || isEmptyBottom || isNewRow,
  });

  const sortableStyle = {
    transform: CSS.Transform.toString(sortableTransform),
    transition: sortableTransition,
  };

  const pending = pendingChanges.get(row.id);
  const evaluated = evaluatedPrices[row.id];

  const getFieldValue = (field: keyof TierEditValues): string => {
    if (pending) return pending[field] as string;
    if (inlineRowData) return inlineRowData.tierEditValues[field] as string;
    if (!row.tier) return '';
    switch (field) {
      case 'maxAmount':
        return row.tier.maxAmount != null ? String(row.tier.maxAmount) : '';
      case 'targetPrice':
        return row.tier.targetPrice.formula ?? '';
      case 'minPrice':
        return row.tier.minPrice.formula ?? '';
      case 'maxPrice':
        return row.tier.maxPrice.formula ?? '';
      case 'scrapValue':
        return String(row.tier.scrapValue);
      default:
        return '';
    }
  };

  const handleChange = (field: string, value: string) => {
    if (isNewRow && inlineRowData) {
      onInlineRowChange(row.id, field, value);
    } else {
      onCellChange(row.id, row.itemPricingId, field, value);
    }
  };

  // Callbacks
  const handleMaterialChange = React.useCallback(
    (value: string | undefined, entity?: SelectableMaterial | undefined) => {
      if (!value || !entity) return;
      const id = entity.id;
      const name = entity.name;
      const effectiveUnit = entity.effectivePricingUnit as string | undefined;
      const groupName = entity.itemGroup?.name ?? undefined;
      if (id === row.itemId) {
        onExitEditing?.();
        return;
      }
      if (isNewRow) {
        onInlineRowChange(row.id, 'product', {
          id,
          name,
          pricingUnit: effectiveUnit,
          itemGroupName: groupName,
        });
        onExitEditing?.();
      } else {
        onCellChange(row.id, row.itemPricingId, 'itemId', id);
        onCellChange(row.id, row.itemPricingId, 'itemName', name);
        if (effectiveUnit) {
          onCellChange(row.id, row.itemPricingId, 'pricingUnit', effectiveUnit);
        }
        setTimeout(() => onTabNavigate?.('right'), 50);
      }
    },
    [
      isNewRow,
      row.id,
      row.itemId,
      row.itemPricingId,
      onInlineRowChange,
      onCellChange,
      onExitEditing,
      onTabNavigate,
    ],
  );

  const handleUnitChange = React.useCallback(
    (value: Unit | undefined) => {
      if (!value || value === row.pricingUnit) {
        onExitEditing?.();
        return;
      }
      if (isNewRow) {
        onInlineRowChange(row.id, 'pricingUnit', value);
      } else {
        onCellChange(row.id, row.itemPricingId, 'pricingUnit', value);
      }
      onTabNavigate?.('right');
    },
    [
      isNewRow,
      row.id,
      row.pricingUnit,
      row.itemPricingId,
      onInlineRowChange,
      onCellChange,
      onTabNavigate,
    ],
  );

  const isEditing = (col: number) =>
    editingState?.row === rowIndex && editingState.col === col;

  const effectiveDragHandleProps = isDragDisabled
    ? {}
    : {
        ...sortableListeners,
        ...sortableAttributes,
        ...(dragHandleProps ?? {}),
      };

  return (
    <tr
      ref={setSortableRef}
      style={sortableStyle}
      className={cn(
        '@SpreadsheetRowInner outline-none',
        pending && 'bg-warning/5',
        hasValidationError &&
          'bg-failure/5 outline outline-1 outline-failure/50',
      )}
      data-row-index={rowIndex}
    >
      <td className="w-10 border border-neutral/20 px-1 py-0">
        {isFirstTier && !isEmptyBottom && (
          <div className="flex items-center justify-center gap-0.5">
            {onSelect && !isReadOnly && (
              <input
                type="checkbox"
                checked={isSelected ?? false}
                onChange={() => onSelect(rowIndex)}
                className="size-3.5 cursor-pointer accent-primary"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {!isReadOnly && (
              <div
                className={cn(
                  'flex items-center justify-center',
                  isDragDisabled && 'opacity-20',
                  !isDragDisabled && 'cursor-grab opacity-50',
                )}
                {...effectiveDragHandleProps}
              >
                <LucideGripVertical className="size-4" />
              </div>
            )}
          </div>
        )}
      </td>

      <SpreadsheetCell
        focused={focusState.row === rowIndex && focusState.col === 0}
        {...getCommonProps(rowIndex, 0)}
        tdClassName=""
      >
        {isReadOnly || isSubsequentTier ? (
          <div className="flex w-full items-center px-2 py-1 text-sm font-medium">
            <span>{pending?.itemName ?? row.itemName}</span>
            {row.tierCount > 1 && (
              <span className="ml-auto font-normal text-neutral/60">
                {t.pricing.itemPricings.table.tieredPricing}
              </span>
            )}
          </div>
        ) : (
          <div
            className="w-full"
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' ||
                e.key === 'ArrowDown' ||
                e.key === 'ArrowUp'
              ) {
                e.stopPropagation();
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                onTabNavigate?.(e.shiftKey ? 'left' : 'right');
              }
            }}
          >
            <SelectMaterial
              value={isNewRow ? inlineRowData?.itemId || undefined : row.itemId}
              onChange={handleMaterialChange}
              placeholder={t.pricing.itemPricings.table.selectMaterial}
              className="rounded-none border-0 bg-transparent shadow-none ring-0"
              excludeIds={existingItemIds}
            />
          </div>
        )}
      </SpreadsheetCell>

      <SpreadsheetCell
        focused={focusState.row === rowIndex && focusState.col === 1}
        {...getCommonProps(rowIndex, 1)}
        tdClassName=""
      >
        {isReadOnly || isSubsequentTier ? (
          <div className="px-2 py-1 text-sm">
            {startCase(pending?.pricingUnit ?? row.pricingUnit)}
          </div>
        ) : (
          <div
            className="w-full"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                const active = document.activeElement as HTMLElement;
                if (active && e.currentTarget.contains(active)) {
                  active.dispatchEvent(
                    new MouseEvent('mousedown', {
                      bubbles: true,
                      cancelable: true,
                    }),
                  );
                }
              }
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.stopPropagation();
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                onTabNavigate?.(e.shiftKey ? 'left' : 'right');
              }
            }}
          >
            <SelectEnum<Unit>
              enumValue={enumUnit}
              value={row.pricingUnit}
              onChange={handleUnitChange}
              selectionBoxClassName="border-0 shadow-none ring-0 rounded-none bg-transparent"
            />
          </div>
        )}
      </SpreadsheetCell>

      {isCRV ? (
        <SpreadsheetCell
          focused={focusState.row === rowIndex && focusState.col === 2}
          {...getCommonProps(rowIndex, 2)}
          tdClassName={cn('min-w-[90px]')}
        >
          <div className="w-full px-2 py-1 text-right text-sm text-neutral/60">
            {row.minWeight != null
              ? `${row.minWeight} ${humanReadableShortPricingUnit(row.pricingUnit)}`
              : '—'}
          </div>
        </SpreadsheetCell>
      ) : (
        <>
          <SpreadsheetCell
            focused={focusState.row === rowIndex && focusState.col === 2}
            {...getCommonProps(rowIndex, 2)}
            tdClassName=""
          >
            <div className="w-full px-2 py-1 text-right text-sm text-neutral/60">
              {row.minWeight != null
                ? `${row.minWeight} ${humanReadableShortPricingUnit(row.pricingUnit)}`
                : '—'}
            </div>
          </SpreadsheetCell>

          <SpreadsheetCell
            focused={focusState.row === rowIndex && focusState.col === 3}
            {...getCommonProps(rowIndex, 3)}
            tdClassName=""
          >
            {isReadOnly || !isEditing(3) ? (
              <div className="w-full px-2 py-1 text-right text-sm">
                {getFieldValue('maxAmount') != null
                  ? getFieldValue('maxAmount') +
                    ' ' +
                    humanReadableShortPricingUnit(row.pricingUnit)
                  : '—'}
              </div>
            ) : (
              <input
                type="text"
                className="w-full bg-transparent px-2 py-1 text-right text-sm outline-none"
                value={getFieldValue('maxAmount')}
                placeholder="—"
                onChange={(e) => handleChange('maxAmount', e.target.value)}
              />
            )}
          </SpreadsheetCell>

          {(['minPrice', 'targetPrice', 'maxPrice'] as const).map(
            (field, idx) => {
              const col = 4 + idx;
              return (
                <SpreadsheetCell
                  key={field}
                  focused={
                    focusState.row === rowIndex && focusState.col === col
                  }
                  {...getCommonProps(rowIndex, col)}
                >
                  <PriceCellContent
                    rawValue={getFieldValue(field)}
                    evalValue={evaluated?.[field]}
                    originalFormula={row.tier?.[field]?.formula ?? ''}
                    adHocEvaluated={adHocEvaluated}
                    rowId={row.id}
                    field={field}
                    isReadOnly={isReadOnly}
                    isEditing={isEditing(col)}
                    variables={variables}
                    onChange={(v) => handleChange(field, v)}
                  />
                </SpreadsheetCell>
              );
            },
          )}
        </>
      )}

      <td className="w-10 border border-neutral/20 px-1 py-0">
        {isFirstTier && !isEmptyBottom && !isNewRow && !isReadOnly && (
          <RowActionMenu
            isCRV={isCRV}
            onAddTier={
              onAddTier ? () => onAddTier(row.itemPricingId) : undefined
            }
            onCopy={onCopy ? () => onCopy(row.itemPricingId) : undefined}
            onDelete={onDelete ? () => onDelete(row.itemPricingId) : undefined}
            t={t}
          />
        )}
        {!isFirstTier && !isReadOnly && onDeleteTier && (
          <div className="flex justify-center">
            <button
              type="button"
              className="rounded p-0.5 text-neutral/40 hover:bg-neutral/10 hover:text-failure"
              onClick={() => onDeleteTier(row.id)}
            >
              <LucideX className="size-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const arePropsEqual = (
  prev: SpreadsheetRowProps,
  next: SpreadsheetRowProps,
): boolean => {
  const prevIsFocused = prev.focusState.row === prev.rowIndex;
  const nextIsFocused = next.focusState.row === next.rowIndex;
  if (prevIsFocused || nextIsFocused) return false;

  const prevIsEditing = prev.editingState?.row === prev.rowIndex;
  const nextIsEditing = next.editingState?.row === next.rowIndex;
  if (prevIsEditing || nextIsEditing) return false;

  if (prev.row !== next.row) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.evaluatedPrices !== next.evaluatedPrices) return false;
  if (prev.adHocEvaluated !== next.adHocEvaluated) return false;
  if (prev.isReadOnly !== next.isReadOnly) return false;
  if (prev.isCRV !== next.isCRV) return false;
  if (prev.isDragDisabled !== next.isDragDisabled) return false;
  if (prev.inlineRowData !== next.inlineRowData) return false;
  if (prev.pendingChanges !== next.pendingChanges) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.hasValidationError !== next.hasValidationError) return false;
  if (prev.existingItemIds !== next.existingItemIds) return false;

  return true;
};

export const SpreadsheetRow = React.memo(SpreadsheetRowInner, arePropsEqual);
